import type {
    EButtonStyleType,
    EButtonWidthType,
    EContentSources,
    ELanguages,
    ESiteStatuses,
    ESize,
} from '@common/enums';
import type { ThemedBaseButtonProp } from './base-button-widget.type';
import type { ThemeButtonHoverLegacy as ThemeButtonHover } from '@sites/entity-core';
import type { DateTime, Id, Uuid } from './helpers';
import type { Domain } from './domain.type';
import type { SitePageListEntity } from './site-page.type';
import type { ButtonIcon } from '@components/sp-button-icon';
import type { ButtonAdditionalEffectsSettings } from '@components/sp-button-additional-effects-settings';
import type { AdvancedRadiusSettings } from '@components/sp-advanced-radius-settings/type/sp-advanced-radius-settings.type';
import type { AdvancedBorderSettings } from '@components/sp-advanced-border-settings/type/advanced-border-settings.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

/**
 * @deprecated
 */
export type Site = {
    id: Id | Uuid;
    hash: Uuid;
    name: string;
    // pages: SitePage[];
    status: ESiteStatuses;
    contents: Contents;
    settings?: SiteSettings;
    domains?: Domain[];
    default_domain?: Domain;
    create_date: DateTime;
    pause_date: DateTime;
    pub_date: DateTime;
    lang?: ELanguages;
    available_page_count?: number;
};

export type SiteListEntity = {
    id: Id;
    name: string;
    status: ESiteStatuses;
    pages: SitePageListEntity[];
    pub_date: string | null;
};

export type SiteEntity = {
    id: Id | Uuid;
    user_id: Id;
    name: string;
    url: string;
    status: ESiteStatuses;
    available_page_count: number;
    blocking_reason: string;
    moderation_status: number; // TODO: add enum
    pause_date: DateTime | null;
    pub_date: DateTime;
    created_at: DateTime;
    updated_at: DateTime;
};

// export type SitePage = {
//     id: Id | Uuid;
//     name: string;
//     contents: Contents;
//     content_source_id: EContentSources;
//     settings: SitePageSettings;
//     domain: string;
//     sub_domain: string;
//     status: EPageStatuses;
//     created_at: DateTime;
//     updated_at: DateTime;
//     url: string;
//     content_source: SiteContentSource;
// };

export type SiteSettings = {
    favicon: string | null;
    favicon_name: string | null;
    google_analytics: string | null;
    google_api_secret: string;
    is_show_sp_logo: string; // boolean '1' | '0'
    main_page_id: string | null;
    not_found_page_id: string | null;
    yandex_metrika: string | null;
    facebook_pixel: string | null;
    facebook_access_token: string;
    vk_pixel: string | null;
    facebook_meta_tag: string | null;
    web_push_id: string | null;
    disallow_robots: string; // boolean '1' | '0'
    language: ELanguages | null;
    shop: SiteSettingsShop | null;
    favicon_url: string | null;
    is_multilingual: boolean;
    updated_at?: string;
};

export type MiniCartButtonStyles = {
    label: string;
    size: ESize;
    style_type?: EButtonStyleType;
    button_base?: ThemedBaseButtonProp;
    button_hover?: ThemeButtonHover;
    width_type: EButtonWidthType;
    horizontal_form_width_type?: EButtonWidthType;
    width_value: number;
    button_icon: ButtonIcon;
    additional_effects: ButtonAdditionalEffectsSettings;
    is_independent_corners?: boolean;
    advanced_radius?: AdvancedRadiusSettings;
    advanced_border_settings?: AdvancedBorderSettings;
    is_shadow?: boolean;
    shadow_settings?: ShadowSettings;
    is_show_button?: boolean;
};

export type SiteSettingsMiniCartStyles = {
    modal_background_color: string | null;
    modal_border_radius: number | null;
    separator_color: string | null;
    text_color: string | null;
    price_color: string | null;
    old_price_color: string | null;
    delete_button_icon_color: string | null;
    delete_button_bg_color: string | null;
    count_button_text_color: string | null;
    count_button_bg_color: string | null;
    count_button_border_color: string | null;
    count_button_rounding: number | null;
    button_bg_color: string | null;
    button_text_color: string | null;
    button_border_radius: number | null;
    button_settings: MiniCartButtonStyles | null;
};

export type SiteSettingsMiniCart = {
    enabled: boolean | null;
    styles: SiteSettingsMiniCartStyles | null;
};

export type SiteSettingsShop = {
    cart_icon: ShopCartIcon | null;
    cart_icon_draft: ShopCartIcon | null;
    cart_template_id: number | null;
    currency: string | null;
    delivery_variants: ShopDeliveryVariant[] | null;
    redirect_to_cart: boolean | null;
    enabled: boolean | null;
    mini_cart: SiteSettingsMiniCart | null;
};

export type ShopCartIcon = {
    icon_bg_color: string | null;
    icon_color: string | null;
    icon_counter_bg_color: string | null;
    icon_counter_color: string | null;
    icon_size: string | null;
    icon_type: string | null;
    icon_rounding: number | null;
    icon_position_type: string | null;
};

export type ShopDeliveryVariant = {
    name: string;
    description: string;
    price: number;
    isDeliveryCondition: boolean;
    minPrice: number | null;
    maxPrice: number | null;
};

export type Contents = {
    content?: string;
    structure?: string;
    theme_settings?: any;
    theme_settings_style?: string;
    shared_blocks?: ShareBlocks;
    draft_structure?: string;
    draft_theme_settings?: any;
    draft_theme_settings_style?: string;
    draft_shared_blocks?: ShareBlocks;
};

export type SiteCreateRequest = {
    content_source: ContentSourceType;
    content: SiteCreateRequestContent[];
    name?: string;
    hash?: string;
    domain?: string;
};

export type UpdateSiteRequest = {
    name?: string;
    domain: string;
};

// export type PageUpdateRequest = {
//     name?: string; // TODO: REMOVE THIS
//     sub_domain?: string; // TODO: REMOVE THIS
//     domain?: string; // TODO: REMOVE THIS
//     system_domain_id?: number; // TODO: REMOVE THIS
//     url?: string; // TODO: REMOVE THIS
//     content_source: ContentSourceType;
//     content: SiteCreateRequestContent[];
// };

export type SiteCreateRequestContent = {
    name: string;
    value: string;
};

export type SiteContentSource = {
    id: EContentSources;
    name: ContentSourceType;
    created_at: DateTime;
    updated_at: DateTime;
};

export enum ContentSourceType {
    Website = 'website',
    MiniLanding = 'mini_landing',
    Education = 'education',
    WebEducation = 'website_education',
    ChatBot = 'chat_bots',
    PopUp = 'pop_ups',
    WebChatBot = 'website_chat_bots',
    Ecommerce = 'crm_product',
}

export type UnparsedShareBlocks = {
    header: string | null;
    footer: string | null;
};

export type ShareBlocks = {
    header: unknown | null;
    footer: unknown | null;
};
